import React, { useMemo, useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import { useDomain } from "@useDomain";
import "./DynamicOrgNavbar.css";
import api from "@api";

function DynamicOrgNavbar({ page, onReady }) {
   const { slug, domain, organization: org, isCustomDomain } = useDomain();
   const [isOpen, setIsOpen] = useState(false);
   const [isClosing, setIsClosing] = useState(false);
   const [pages, setPages] = useState([]);
   const [navbarSettings, setNavbarSettings] = useState(null);

   const fallbackSlug = window.location.pathname.split("/")[1];
   const basePath = isCustomDomain
      ? ""
      : slug
         ? `/${slug}`
         : fallbackSlug
            ? `/${fallbackSlug}`
            : "";

   useEffect(() => {
      let alive = true;
      const loadNavPages = async () => {
         try {
            if (!slug && !domain && !org?.id) {
               setPages([]);
               setNavbarSettings(null);
               return;
            }
            
            const res = await api.get("/api/organizations/public/nav-pages", {
               params: {
                  slug: slug || undefined,
                  domain: domain || undefined,
                  organizationId: org?.id || undefined,
               },
            });

            if (!alive) return;

            setPages(res.data?.pages || []);
            setNavbarSettings(res.data?.settings || null);
            onReady?.();
         } catch (error) {
            if (!alive) return;
            setPages([]);
            setNavbarSettings(null);
            onReady?.();
         }
      };
      loadNavPages();
      return () => {
         alive = false;
      };
   }, [slug, domain, org?.id]);

   const navItems = useMemo(() => {
      const isHomepagePage = (page) => page.is_homepage || !page.path;
      const publishedPages = pages.filter((page) => page.is_published !== false);
      const homepagePages = publishedPages.filter(isHomepagePage);
      const nonHomepagePages = publishedPages.filter((page) => !isHomepagePage(page));

      const shouldHideHomepageLink =
         homepagePages.length === 1 && nonHomepagePages.length === 0;

      const pageItems = publishedPages
         .filter((page) => !(shouldHideHomepageLink && isHomepagePage(page)))
         .map((page) => {
            const isHomepage = isHomepagePage(page);
            return {
               key: `page-${page.id}`,
               label: page.name || "Page",
               to: isHomepage ? (basePath || "/") : `${basePath}/${page.path}`,
               isExternal: false,
            };
         });
      if (org?.registration_url) {
         pageItems.push({
            key: "register-external",
            label: org?.registration_label || "Register",
            to: org.registration_url,
            isExternal: true,
         });
      }
      return pageItems;
   }, [pages, basePath, org?.registration_url, org?.registration_label]);


   const location = useLocation();
   const isHomepage =
      location.pathname === (basePath || "/") ||
      (isCustomDomain && location.pathname === "/");
   // const navbarBackgroundMode = isHomepage ? "transparent" : "solid";
   // const rawNavbarTheme =
   //    page?.content?.navbarTheme ||
   //    page?.pageSettings?.content?.navbarTheme ||
   //    org?.theme_mode ||
   //    "light";
   // const navbarTheme =
   //    String(rawNavbarTheme).trim().toLowerCase() === "dark" ? "dark" : "light";
   // const isFastActionBasketball =
   //    org?.slug === "fastactionbasketball" ||
   //    slug === "fastactionbasketball" ||
   //    org?.domain === "fastactionbasketball.com" ||
   //    domain === "fastactionbasketball.com";
   // const forceWhiteHomepageLinks = isHomepage && isFastActionBasketball;
   // const finalNavbarTheme = forceWhiteHomepageLinks ? "dark" : navbarTheme;

   const navbarHeight = Number(navbarSettings?.navbar_height) || 100;
   const navbarLogoHeight =
      Number(navbarSettings?.navbar_logo_height) || 84;
   const homepageMode =
      navbarSettings?.navbar_home_mode === "overlay"
         ? "overlay"
         : "solid";

   const isHomepageOverlay =
      isHomepage && homepageMode === "overlay";

   const navbarBackgroundColor = isHomepageOverlay
      ? "transparent"
      : isHomepage
         ? navbarSettings?.navbar_home_background_color || "#ffffff"
         : navbarSettings?.navbar_inner_background_color || "#ffffff";

   const navbarBorderColor = isHomepage
      ? navbarSettings?.navbar_home_border_color || "transparent"
      : navbarSettings?.navbar_inner_border_color || "transparent";

   const navbarLinkColor = isHomepage
      ? navbarSettings?.navbar_home_link_color || "#111111"
      : navbarSettings?.navbar_inner_link_color || "#111111";
   const closeMenu = () => {
      setIsClosing(true);
      setTimeout(() => {
         setIsOpen(false);
         setIsClosing(false);
      }, 150);
   };

   const toggleMenu = () => {
      if (isOpen) {
         closeMenu();
      } else {
         setIsOpen(true);
      }
   };

   return (
      <nav
         className={`navbar_container ${isHomepageOverlay
               ? "navbar-position-overlay"
               : "navbar-position-solid"
            }`}
         style={{
            "--org-navbar-height": `${navbarHeight}px`,
            "--org-navbar-logo-height": `${navbarLogoHeight}px`,
            "--org-navbar-background": navbarBackgroundColor,
            "--org-navbar-link-color": navbarLinkColor,
            "--org-navbar-border-color": navbarBorderColor,
         }}
      >
         <div className="navbar_left">
            {org?.logo_url ? (
               <NavLink to={basePath || "/"} className="navbar_logo_link" onClick={closeMenu}>
                  <img
                     src={org.logo_url}
                     alt={org?.name || "Logo"}
                     className="navbar_logo_image"
                  />
               </NavLink>
            ) : null}
         </div>

         <div className="navbar_right">
            <button
               className={`navbar_hamburger ${isOpen ? "menu-open" : ""}`}
               onClick={toggleMenu}
               type="button"
               aria-label={isOpen ? "Close menu" : "Open menu"}
               aria-expanded={isOpen}
            >
               {isOpen ? <FaTimes /> : <FaBars />}
            </button>

            <ul className={`navbar_list ${isOpen ? "open" : ""} ${isClosing ? "closing" : ""}`}>
               {navItems.map((item) => (
                  <li key={item.key}>
                     {item.isExternal ? (
                        <a
                           href={item.to}
                           target="_blank"
                           rel="noopener noreferrer"
                           onClick={closeMenu}
                        >
                           {item.label}
                        </a>
                     ) : (
                        <NavLink to={item.to} onClick={closeMenu}>
                           {item.label}
                        </NavLink>
                     )}
                  </li>
               ))}
            </ul>
         </div>
      </nav>
   );
}

export default DynamicOrgNavbar;